import React, { useEffect, useState } from 'react';
import { Progress } from 'antd';
import { 
  CheckCircleFilled, 
  WarningFilled, 
  ExclamationCircleFilled, 
  SafetyCertificateOutlined,
  ThunderboltFilled
} from '@ant-design/icons';

const WhatsAppCampaignHealthPanel = ({ phoneNumbers, selectedPhoneId, audienceCount, isTemplateSelected, isAudienceSelected }) => {
  const [quota, setQuota] = useState(null);
  
  useEffect(() => {
    if (!selectedPhoneId) {
      if (phoneNumbers && phoneNumbers.length > 0) {
        setQuota(phoneNumbers[0].quotaSnapshot || {});
      }
      return;
    }
    const phone = phoneNumbers?.find(p => p.id === selectedPhoneId);
    if (phone) {
      setQuota(phone.quotaSnapshot || {});
    }
  }, [selectedPhoneId, phoneNumbers]);

  if (!quota) return null;

  const tierLimit = quota.tierLimit || 0;
  const estimatedOpened = quota.estimatedOpened || 0;
  const projectedTotal = estimatedOpened + (audienceCount || 0);
  
  const percentUsed = tierLimit > 0 ? Math.min(100, Math.round((projectedTotal / tierLimit) * 100)) : 0;
  const estimatedRemaining = Math.max(0, tierLimit - projectedTotal);
  const exceedsLimit = projectedTotal > tierLimit;
  
  const riskLevel = exceedsLimit ? 'high' : percentUsed >= 80 ? 'medium' : 'low';

  const getStatusBadge = () => {
    if (exceedsLimit) {
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-font-semibold tw-bg-red-50 tw-text-red-700 tw-border tw-border-solid tw-border-red-200/60">
          <span className="tw-size-1.5 tw-rounded-full tw-bg-red-600 tw-animate-pulse" />
          Exceeded
        </span>
      );
    }
    if (riskLevel === 'medium') {
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-font-semibold tw-bg-amber-50 tw-text-amber-700 tw-border tw-border-solid tw-border-amber-200/60">
          <span className="tw-size-1.5 tw-rounded-full tw-bg-amber-500" />
          Near Limit
        </span>
      );
    }
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-font-semibold tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-solid tw-border-emerald-200/60">
        <span className="tw-size-1.5 tw-rounded-full tw-bg-emerald-500" />
        Healthy
      </span>
    );
  };

  return (
    <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-p-5 tw-shadow-sm tw-sticky tw-top-5 tw-transition-all hover:tw-shadow-md">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-4 tw-mb-4 tw-border-b tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-slate-100">
        <div className="tw-flex tw-items-center tw-gap-2.5">
          <div className="tw-size-8 tw-rounded-xl tw-bg-[#5D44FF]/10 tw-text-[#5D44FF] tw-flex tw-items-center tw-justify-center">
            <SafetyCertificateOutlined className="tw-text-lg" />
          </div>
          <div>
            <h3 className="tw-text-base tw-font-bold tw-text-slate-800 tw-m-0 tw-leading-tight">Campaign Health</h3>
            <p className="tw-text-[11px] tw-text-slate-400 tw-m-0 tw-mt-0.5">Real-time quota & readiness</p>
          </div>
        </div>
        {getStatusBadge()}
      </div>
      
      {/* Key Metrics */}
      <div className="tw-space-y-2.5 tw-mb-5">
        <div className="tw-flex tw-justify-between tw-items-center tw-text-xs">
          <span className="tw-text-slate-500 tw-font-medium">Audience Size</span>
          <span className="tw-font-bold tw-text-slate-800 tw-bg-slate-50 tw-px-2 tw-py-0.5 tw-rounded-md tw-border tw-border-solid tw-border-slate-100">
            {audienceCount?.toLocaleString() || 0}
          </span>
        </div>
        <div className="tw-flex tw-justify-between tw-items-center tw-text-xs">
          <span className="tw-text-slate-500 tw-font-medium">Messaging Tier</span>
          <span className="tw-font-bold tw-text-slate-800 tw-bg-slate-50 tw-px-2 tw-py-0.5 tw-rounded-md tw-border tw-border-solid tw-border-slate-100">
            {tierLimit?.toLocaleString() || 'N/A'}
          </span>
        </div>
        <div className="tw-flex tw-justify-between tw-items-center tw-text-xs">
          <span className="tw-text-slate-500 tw-font-medium">Current 24h Usage</span>
          <span className="tw-font-bold tw-text-slate-800 tw-bg-slate-50 tw-px-2 tw-py-0.5 tw-rounded-md tw-border tw-border-solid tw-border-slate-100">
            {estimatedOpened?.toLocaleString() || 0}
          </span>
        </div>

        {/* Highlighted Projected Total */}
        <div className={`tw-p-3 tw-rounded-xl tw-border tw-border-solid tw-flex tw-justify-between tw-items-center tw-mt-1 ${
          exceedsLimit 
            ? 'tw-bg-red-50/60 tw-border-red-200 tw-text-red-900' 
            : 'tw-bg-indigo-50/50 tw-border-indigo-100 tw-text-slate-900'
        }`}>
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <ThunderboltFilled className={exceedsLimit ? 'tw-text-red-500' : 'tw-text-[#5D44FF]'} />
            <span className="tw-text-xs tw-font-bold">Projected New Total</span>
          </div>
          <span className={`tw-text-sm tw-font-extrabold ${exceedsLimit ? 'tw-text-red-600' : 'tw-text-[#5D44FF]'}`}>
            {projectedTotal.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Usage Progress */}
      <div className="tw-bg-slate-50/70 tw-p-3.5 tw-rounded-xl tw-border tw-border-solid tw-border-slate-100 tw-mb-5">
        <div className="tw-flex tw-justify-between tw-items-center tw-text-xs tw-font-bold tw-mb-1.5">
          <span className="tw-text-slate-600">Capacity Usage</span>
          <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[11px] ${
            exceedsLimit ? 'tw-bg-red-100 tw-text-red-700' : riskLevel === 'medium' ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-bg-emerald-100 tw-text-emerald-700'
          }`}>
            {percentUsed}%
          </span>
        </div>
        <Progress 
          percent={percentUsed} 
          showInfo={false} 
          size="small"
          status={exceedsLimit ? 'exception' : 'normal'}
          strokeColor={exceedsLimit ? '#dc2626' : riskLevel === 'medium' ? '#f59e0b' : '#10b981'} 
          className="!tw-m-0"
        />
        <div className="tw-flex tw-justify-between tw-items-center tw-text-[11px] tw-text-slate-500 tw-mt-2.5 tw-pt-2 tw-border-t tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-solid tw-border-slate-200/60">
          <span>Estimated Remaining</span>
          <span className="tw-font-bold tw-text-slate-700">{estimatedRemaining.toLocaleString()}</span>
        </div>
      </div>
      
      {/* Alert Banners */}
      {exceedsLimit && (
        <div className="tw-bg-red-50 tw-p-3 tw-rounded-xl tw-border tw-border-solid tw-border-red-200/80 tw-flex tw-gap-2.5 tw-items-start tw-mb-5">
          <ExclamationCircleFilled className="tw-text-red-500 tw-text-base tw-mt-0.5 tw-shrink-0" />
          <div className="tw-text-xs tw-text-red-800">
            <strong className="tw-font-bold tw-block tw-text-red-900">Capacity Exceeded</strong>
            <p className="tw-m-0 tw-mt-0.5 tw-text-red-700 tw-leading-snug">
              Audience size exceeds daily tier limit. Some messages may fail to send until tier upgrades.
            </p>
          </div>
        </div>
      )}

      {riskLevel === 'medium' && !exceedsLimit && (
        <div className="tw-bg-amber-50 tw-p-3 tw-rounded-xl tw-border tw-border-solid tw-border-amber-200/80 tw-flex tw-gap-2.5 tw-items-start tw-mb-5">
          <WarningFilled className="tw-text-amber-500 tw-text-base tw-mt-0.5 tw-shrink-0" />
          <div className="tw-text-xs tw-text-amber-800">
            <strong className="tw-font-bold tw-block tw-text-amber-900">Approaching Limit</strong>
            <p className="tw-m-0 tw-mt-0.5 tw-text-amber-700 tw-leading-snug">
              This campaign will utilize most of your remaining daily capacity.
            </p>
          </div>
        </div>
      )}

      {/* Pre-launch Checklist */}
      <div className="tw-pt-4 tw-border-t tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-solid tw-border-slate-100">
        <h4 className="tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-3">Pre-launch Checklist</h4>
        
        <div className="tw-space-y-2">
          <div className="tw-flex tw-items-center tw-justify-between tw-p-2.5 tw-rounded-xl tw-bg-emerald-50/60 tw-border tw-border-solid tw-border-emerald-100/80">
            <div className="tw-flex tw-items-center tw-gap-2">
              <CheckCircleFilled className="tw-text-emerald-500 tw-text-sm" />
              <span className="tw-text-xs tw-font-semibold tw-text-emerald-950">WhatsApp Connected</span>
            </div>
            <span className="tw-text-[10px] tw-font-bold tw-text-emerald-600 tw-uppercase tw-tracking-wider">Ready</span>
          </div>

          <div className={`tw-flex tw-items-center tw-justify-between tw-p-2.5 tw-rounded-xl tw-border tw-border-solid tw-transition-all ${
            isAudienceSelected 
              ? 'tw-bg-emerald-50/60 tw-border-emerald-100/80' 
              : 'tw-bg-slate-50/60 tw-border-slate-100'
          }`}>
            <div className="tw-flex tw-items-center tw-gap-2">
              {isAudienceSelected ? (
                <CheckCircleFilled className="tw-text-emerald-500 tw-text-sm" />
              ) : (
                <div className="tw-size-3.5 tw-rounded-full tw-border-2 tw-border-solid tw-border-slate-300" />
              )}
              <span className={`tw-text-xs tw-font-semibold ${isAudienceSelected ? "tw-text-emerald-950" : "tw-text-slate-500"}`}>
                Audience Selected
              </span>
            </div>
            <span className={`tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider ${isAudienceSelected ? "tw-text-emerald-600" : "tw-text-slate-400"}`}>
              {isAudienceSelected ? "Ready" : "Pending"}
            </span>
          </div>

          <div className={`tw-flex tw-items-center tw-justify-between tw-p-2.5 tw-rounded-xl tw-border tw-border-solid tw-transition-all ${
            isTemplateSelected 
              ? 'tw-bg-emerald-50/60 tw-border-emerald-100/80' 
              : 'tw-bg-slate-50/60 tw-border-slate-100'
          }`}>
            <div className="tw-flex tw-items-center tw-gap-2">
              {isTemplateSelected ? (
                <CheckCircleFilled className="tw-text-emerald-500 tw-text-sm" />
              ) : (
                <div className="tw-size-3.5 tw-rounded-full tw-border-2 tw-border-solid tw-border-slate-300" />
              )}
              <span className={`tw-text-xs tw-font-semibold ${isTemplateSelected ? "tw-text-emerald-950" : "tw-text-slate-500"}`}>
                Template Selected
              </span>
            </div>
            <span className={`tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider ${isTemplateSelected ? "tw-text-emerald-600" : "tw-text-slate-400"}`}>
              {isTemplateSelected ? "Ready" : "Pending"}
            </span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default WhatsAppCampaignHealthPanel;
